import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { filter, map } from 'rxjs';

import {
  LANGUAGE_NAMES,
  LANGUAGE_SHORT,
  LanguageCode,
  SUPPORTED_LANGUAGES,
} from '../../core/i18n/language';
import { moduleIdFromUrl } from '../../core/modules/module-url';
import { CompanyContextService } from '../../services/company-context.service';
import { LanguageService } from '../../services/language.service';
import { ModuleRegistryService } from '../../services/module-registry.service';
import { SidebarStateService } from '../../services/sidebar-state.service';
import { ThemeService } from '../../services/theme.service';
import { CommandPaletteService } from '../command-palette/command-palette.service';

/** One permission of the open module and whether the user holds it. */
export interface ModulePermission {
  readonly verb: string;
  readonly held: boolean;
}

/** One step of the trail, from the application down to the open screen. */
export interface Crumb {
  /** Translation key — the shell's own for its screens, the module's for theirs. */
  readonly titleKey: string;
  readonly url: string;
}

/**
 * The bar over the content column: where the user is, how to get back, the
 * search, the theme switch and the language switch. Session-wide context — company and user — lives
 * in the sidebar, which spans the full height beside this bar.
 *
 * The trail always starts at the application itself, so a module never looks
 * like the whole product: `SAPHIR Web › Payroll › Employee`. It is built from
 * the activated routes rather than from the address, which is what lets a
 * module contribute its own steps — the shell reads the `title` each route
 * declares and knows nothing else about them.
 */
@Component({
  selector: 'app-shell-header',
  imports: [RouterLink, ButtonDirective, Menu, TranslatePipe],
  templateUrl: './shell-header.html',
  styleUrl: './shell-header.scss',
})
export class ShellHeader {
  private readonly router = inject(Router);
  private readonly theme = inject(ThemeService);
  private readonly language = inject(LanguageService);
  private readonly registry = inject(ModuleRegistryService);
  private readonly companies = inject(CompanyContextService);

  protected readonly sidebar = inject(SidebarStateService);

  private readonly commandPalette = inject(CommandPaletteService);

  protected openPalette(): void {
    this.commandPalette.show();
  }

  private readonly routerState = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.routerState.snapshot),
    ),
    { initialValue: this.router.routerState.snapshot },
  );

  /** The application, then every titled route below it. */
  protected readonly crumbs = computed<Crumb[]>(() => [
    { titleKey: 'app.title', url: '/' },
    ...trailOf(this.routerState().root),
  ]);

  /** The module the address points into, or `undefined` on the shell's own screens. */
  private readonly openModule = computed(() => {
    const id = moduleIdFromUrl(this.routerState().url);

    return id === undefined
      ? undefined
      : this.registry.allModules().find((module) => module.id === id);
  });

  /**
   * The permissions of the open module: its catalogue (`manifest.permissions`)
   * plus whatever the user management issued for it beyond the catalogue, each
   * marked as held or not. Verbs arrive under the module's UMC code
   * (`payroll.read`) and are checked under both spellings the shell exposes.
   */
  protected readonly modulePermissions = computed<ModulePermission[]>(() => {
    const module = this.openModule();

    if (!module) {
      return [];
    }

    const held = new Set(this.companies.activePermissions());
    const code = module.licenseCode ?? module.id;
    const issued =
      this.companies.activeMembership()?.modules.find((access) => access.moduleCode === code)
        ?.permissions ?? [];
    const verbs = [...new Set([...(module.manifest?.permissions ?? []), ...issued])];

    return verbs.map((verb) => ({ verb, held: held.has(verb) }));
  });

  /** `3/6` on the trigger — held over known; hidden while no module is open. */
  protected readonly permissionSummary = computed(() => {
    const permissions = this.modulePermissions();

    return `${permissions.filter((permission) => permission.held).length}/${permissions.length}`;
  });

  protected readonly showsPermissions = computed(() => this.openModule() !== undefined);

  /** One entry per verb, the held ones with the check mark — the same look as the language menu. */
  protected readonly permissionItems = computed<MenuItem[]>(() => {
    const permissions = this.modulePermissions();

    if (permissions.length === 0) {
      return [{ id: 'permission-none', label: 'header.permissions.none', disabled: true }];
    }

    return permissions.map(({ verb, held }) => ({
      id: `permission-${verb}`,
      label: verb,
      icon: held ? 'pi pi-check' : 'pi pi-fw',
      styleClass: held ? 'shell-header__permission--held' : 'shell-header__permission--missing',
    }));
  });

  protected readonly isDark = computed(() => this.theme.colorScheme() === 'dark');

  protected toggleTheme(): void {
    this.theme.toggle();
  }

  /** The two letters on the trigger — the language names itself, never translated. */
  protected readonly languageCode = computed(() => LANGUAGE_SHORT[this.language.current()]);

  /**
   * A menu rather than a toggle, like the UMC's: two languages would fit a
   * toggle, a third would turn it into a rewrite. Each language is written in
   * itself — "Deutsch", not "German": whoever looks for it cannot read the
   * other language. The one in use carries the check mark.
   */
  protected readonly languageItems = computed<MenuItem[]>(() => {
    const current = this.language.current();

    return SUPPORTED_LANGUAGES.map((code) => ({
      id: `language-${code}`,
      label: LANGUAGE_NAMES[code],
      icon: code === current ? 'pi pi-check' : 'pi pi-fw',
      styleClass: code === current ? 'shell-header__language-item--on' : undefined,
      command: () => this.changeLanguage(code),
    }));
  });

  protected changeLanguage(code: LanguageCode): void {
    void this.language.change(code);
  }
}

/**
 * Walks the activated route down its first children, collecting the ones that
 * name themselves.
 *
 * Routes without a title of their own are structural — a path segment that
 * groups children — and contribute to the address but not to the trail.
 *
 * The check is against `routeConfig`, not against the resolved `title`: a
 * componentless route passes its data down, so `employees` under a titled
 * module route reports the module's title and would repeat it as a step.
 */
function trailOf(root: ActivatedRouteSnapshot): Crumb[] {
  const crumbs: Crumb[] = [];
  const segments: string[] = [];

  for (let route = root.firstChild; route; route = route.firstChild) {
    segments.push(...route.url.map((segment) => segment.path));

    if (route.routeConfig?.title !== undefined && route.title) {
      crumbs.push({ titleKey: route.title, url: `/${segments.join('/')}` });
    }
  }

  return crumbs;
}
